import { useMemo } from "react";
import type { Task } from "@/api/tasks";
import { ListViewGroup } from "./list-view-group";
import {
  filterTasksForView,
  useTaskDisplayState,
} from "./task-display-filters";
import {
  TASK_DATE_GROUP_LABEL_KEYS,
  TASK_DATE_GROUPS,
  taskDateGroup,
} from "./task-dates";
import { useT } from "@/i18n";
import { framesTaskboardMessages } from "@/i18n/messages/frames-taskboard";
import { useLocalCalendarDay } from "./use-local-calendar-day";

/** Sort tasks within a group: priority → status → created date. */
const PRIORITY_ORDER: Record<string, number> = {
  urgent: 0,
  high: 1,
  medium: 2,
  low: 3,
};
const STATUS_ORDER: Record<string, number> = {
  blocked: 0,
  in_progress: 1,
  in_review: 2,
  todo: 3,
  backlog: 4,
  done: 5,
};

function sortTasks(tasks: Task[], sortBy: string, sortDir: string): Task[] {
  const dir = sortDir === "asc" ? 1 : -1;
  return [...tasks].sort((a, b) => {
    switch (sortBy) {
      case "priority": {
        const pa = PRIORITY_ORDER[a.priority] ?? 3;
        const pb = PRIORITY_ORDER[b.priority] ?? 3;
        return (pa - pb) * dir;
      }
      case "due_date": {
        const da = a.due_at ? new Date(a.due_at).getTime() : Infinity;
        const db = b.due_at ? new Date(b.due_at).getTime() : Infinity;
        return (da - db) * dir;
      }
      case "created": {
        const ca = new Date(a.created_at).getTime();
        const cb = new Date(b.created_at).getTime();
        return (ca - cb) * dir;
      }
      case "status": {
        const sa = STATUS_ORDER[a.status] ?? 5;
        const sb = STATUS_ORDER[b.status] ?? 5;
        return (sa - sb) * dir;
      }
      case "agent": {
        const na = a.assigned_agent_detail?.name ?? "";
        const nb = b.assigned_agent_detail?.name ?? "";
        return na.localeCompare(nb) * dir;
      }
      default:
        return 0;
    }
  });
}

interface ListViewProps {
  tasks: Task[];
  onSelectTask: (taskId: string) => void;
}
export function ListView({ tasks, onSelectTask }: ListViewProps) {
  const t = useT(framesTaskboardMessages);
  const display = useTaskDisplayState();
  const now = useLocalCalendarDay();
  const { groups, childrenByParent } = useMemo(() => {
    const filtered = sortTasks(
      filterTasksForView(tasks, display, "list"),
      display.sortBy,
      display.sortDirection,
    );
    const ids = new Set(filtered.map((task) => task.id));
    const childrenByParent = new Map<string, Task[]>();
    for (const task of filtered) {
      if (!task.parent_task || !ids.has(task.parent_task)) continue;
      const children = childrenByParent.get(task.parent_task) ?? [];
      children.push(task);
      childrenByParent.set(task.parent_task, children);
    }
    const roots = filtered.filter(
      (task) => !task.parent_task || !ids.has(task.parent_task),
    );
    const groups = TASK_DATE_GROUPS.map((group) => ({
      group,
      tasks: roots.filter((task) => taskDateGroup(task.due_at, now) === group),
    })).filter((group) => group.tasks.length > 0);
    return { groups, childrenByParent };
  }, [tasks, display, now]);
  return (
    <div className="flex h-full flex-col" data-testid="list-view">
      <div className="scrollbar-none min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
        {groups.length ? (
          groups.map((group) => (
            <ListViewGroup
              key={group.group}
              id={group.group}
              label={t(TASK_DATE_GROUP_LABEL_KEYS[group.group])}
              tasks={group.tasks}
              onSelectTask={onSelectTask}
              childrenByParent={childrenByParent}
            />
          ))
        ) : (
          <div className="py-12 text-center text-appkit-body text-muted">
            {t("noTasks")}
          </div>
        )}
      </div>
    </div>
  );
}
